import { useEffect, useRef } from 'react';

type Props = {
  /** Peak amplitude per bucket, or `null` to draw the waiting state */
  peaks: ReadonlyArray<number> | null;
  /** Share of the clip already played, in `[0, 1]` */
  progress: number;
  /** Drawn height in CSS pixels; the width follows the container */
  height: number;
  /** Color of the bars still to be played */
  baseColor: string;
  /** Left-to-right gradient stops of the bars already played */
  progressColors: ReadonlyArray<string>;
  barWidth?: number;
  barGap?: number;
  className?: string;
};

/**
 * ### PeaksWaveform
 *
 * Draws a clip as centred bars from its peak buckets, the played part in a
 * gradient and the rest in one color, the way the console's players draw
 * theirs. Redraws on every progress change and on resize; while the peaks
 * are still loading it draws a faint centre line so the box keeps its size.
 *
 * @param props Peaks, progress, size, and colors
 * @returns The rendered canvas element
 */
export default function PeaksWaveform({
  peaks,
  progress,
  height,
  baseColor,
  progressColors,
  barWidth = 2,
  barGap = 1,
  className,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const context = canvas.getContext('2d');
      if (!context) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = canvas.clientWidth;
      if (width === 0) return;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);

      const mid = height / 2;

      if (!peaks) {
        context.fillStyle = baseColor;
        context.globalAlpha = 0.5;
        context.fillRect(0, mid - 0.5, width, 1);
        context.globalAlpha = 1;
        return;
      }

      const slot = barWidth + barGap;
      const bars = Math.max(1, Math.floor(width / slot));
      const perBar = peaks.length / bars;
      const loudest = Math.max(...peaks, 0.001);
      const playedX = progress * width;

      const gradient = context.createLinearGradient(0, 0, width, 0);
      progressColors.forEach((color, index) => {
        gradient.addColorStop(
          progressColors.length === 1 ? 0 : index / (progressColors.length - 1),
          color,
        );
      });

      for (let bar = 0; bar < bars; bar += 1) {
        const from = Math.floor(bar * perBar);
        const to = Math.max(from + 1, Math.floor((bar + 1) * perBar));
        let peak = 0;
        for (let index = from; index < to; index += 1) {
          peak = Math.max(peak, peaks[index] ?? 0);
        }
        const half = Math.max(1, (peak / loudest) * (mid - 1));
        const x = bar * slot;

        context.fillStyle = x + barWidth <= playedX ? gradient : baseColor;
        context.beginPath();
        context.roundRect(x, mid - half, barWidth, half * 2, barWidth / 2);
        context.fill();
      }
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);

    return () => observer.disconnect();
  }, [peaks, progress, height, baseColor, progressColors, barWidth, barGap]);

  return (
    <canvas
      ref={canvasRef}
      className={className ?? 'block w-full'}
      style={{ height }}
      aria-hidden="true"
    />
  );
}
